import { useEffect, useState } from 'react';
import Reveal from '@/components/feature/Reveal';
import Stars from '@/components/base/Stars';
import GoogleWordmark from '@/components/base/GoogleWordmark';
import { useInView } from '@/hooks/useInView';
import { useCountUp } from '@/hooks/useCountUp';
import { reviews } from '@/mocks/reviews';
import { site } from '@/data/site';

interface ReviewCardProps {
  review: (typeof reviews)[number];
  onOpen: () => void;
}

function ReviewCard({ review, onOpen }: ReviewCardProps) {
  const hasText = review.text.trim().length > 0;
  const isLong = review.text.length > 110;
  const initial = review.name.charAt(0).toUpperCase();

  return (
    <article className="w-[300px] sm:w-[340px] md:w-[372px] mr-4 md:mr-5 shrink-0 h-[300px] md:h-[320px] flex flex-col rounded-xl border border-background-50/12 bg-background-50/[0.05] p-6 transition-all duration-500 hover:border-accent-500/45 hover:bg-background-50/[0.09]">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 shrink-0 rounded-full bg-accent-500 flex items-center justify-center font-heading text-lg font-bold text-foreground-950">
            {initial}
          </span>
          <div className="min-w-0">
            <p className="font-label text-sm font-bold text-background-50 truncate">{review.name}</p>
            <p className="font-label text-xs text-background-100/55 truncate">
              {review.meta ? `${review.meta} · ${review.time}` : review.time}
            </p>
          </div>
        </div>
        <GoogleWordmark className="text-sm shrink-0 pt-1" />
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Stars size="text-base" />
        <span className="font-label text-xs font-semibold text-background-100/70">5,0</span>
      </div>

      <div className="mt-4 flex-1 overflow-hidden">
        {hasText ? (
          <p className={`font-body text-sm leading-relaxed text-background-100/80 whitespace-pre-line ${isLong ? 'clamp-3' : ''}`}>
            {review.text}
          </p>
        ) : (
          <p className="font-body text-sm italic text-background-100/55">
            Bewertung ohne Kommentar – volle Sternezahl.
          </p>
        )}
      </div>

      {isLong && (
        <button
          type="button"
          onClick={onOpen}
          className="mt-3 self-start inline-flex items-center gap-1 font-label text-sm font-bold text-accent-400 hover:text-accent-300 transition-colors cursor-pointer whitespace-nowrap"
        >
          Mehr anzeigen
          <i className="ri-arrow-right-s-line text-base" />
        </button>
      )}
    </article>
  );
}

export default function Reviews() {
  const [openReview, setOpenReview] = useState<(typeof reviews)[number] | null>(null);
  const { ref, inView } = useInView<HTMLDivElement>(0.4);
  const rating = useCountUp(5, inView, 1700);
  const ratingLabel = rating.toFixed(1).replace('.', ',');
  const loop = [...reviews, ...reviews];

  useEffect(() => {
    if (!openReview) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenReview(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openReview]);

  return (
    <section id="bewertungen" className="relative bg-primary-950 py-20 md:py-28 scroll-mt-24 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_25%_15%,oklch(var(--accent-500))_0,transparent_40%),radial-gradient(circle_at_80%_85%,oklch(var(--secondary-400))_0,transparent_42%)]" />

      <div className="relative w-full px-4 md:px-8">
        <Reveal className="max-w-3xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 font-label text-xs font-semibold uppercase tracking-[0.32em] text-accent-400">
            <span className="w-6 h-px bg-accent-500" />
            Kundenstimmen
            <span className="w-6 h-px bg-accent-500" />
          </span>
          <div ref={ref} className="mt-6 flex flex-col items-center">
            <span className="font-heading text-6xl md:text-8xl font-semibold leading-none text-background-50 tabular-nums">
              {ratingLabel}
            </span>
            <Stars size="text-3xl md:text-4xl mt-5" animated start={inView} />
          </div>
          <h2 className="mt-6 font-heading text-2xl md:text-4xl font-semibold text-background-50">
            auf <GoogleWordmark className="text-2xl md:text-4xl" /> Bewertungen
          </h2>
          <p className="mt-5 font-body text-base md:text-lg leading-relaxed text-background-100/70">
            Echte Bewertungen unserer Kundinnen und Kunden aus der Region – ungekürzt und wörtlich
            übernommen.
          </p>
        </Reveal>
      </div>

      <div className="group relative mt-12">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-32 z-10 bg-gradient-to-r from-primary-950 to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-32 z-10 bg-gradient-to-l from-primary-950 to-transparent" />

        <div className="overflow-hidden py-2">
          <div className={`marquee-track items-stretch ${openReview ? 'is-paused' : ''}`}>
            {loop.map((review, index) => (
              <ReviewCard
                key={`${review.id}-${index}`}
                review={review}
                onOpen={() => setOpenReview(review)}
              />
            ))}
          </div>
        </div>

        <style>{`.group:hover .marquee-track { animation-play-state: paused; }`}</style>
      </div>

      <Reveal className="mt-12 text-center px-4">
        <a
          href="#kontakt"
          className="inline-flex items-center gap-2 rounded-md bg-accent-500 hover:bg-accent-400 text-foreground-950 font-label font-bold text-base px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-leaf-line text-lg" />
          Jetzt Termin anfragen
        </a>
      </Reveal>

      {/* Overlay: full review */}
      {openReview && (
        <div
          className="overlay-backdrop fixed inset-0 z-[70] flex items-center justify-center p-4 bg-primary-950/80 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={`Bewertung von ${openReview.name}`}
          onClick={() => setOpenReview(null)}
        >
          <div
            className="overlay-card relative w-full max-w-lg max-h-[82vh] overflow-y-auto rounded-2xl bg-background-50 p-7 md:p-9 shadow-[0_40px_120px_-40px_rgba(0,0,0,0.7)]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpenReview(null)}
              aria-label="Schließen"
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-200 hover:text-foreground-900 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl" />
            </button>

            <div className="flex items-center gap-3 pr-10">
              <span className="w-12 h-12 shrink-0 rounded-full bg-primary-500 flex items-center justify-center font-heading text-xl font-bold text-background-50">
                {openReview.name.charAt(0).toUpperCase()}
              </span>
              <div className="min-w-0">
                <p className="font-heading text-lg font-bold text-foreground-950 truncate">
                  {openReview.name}
                </p>
                <p className="font-label text-xs text-foreground-500">
                  {openReview.meta ? `${openReview.meta} · ${openReview.time}` : openReview.time}
                </p>
              </div>
            </div>

            <div className="mt-5 flex items-center gap-2.5">
              <Stars size="text-lg" />
              <span className="font-label text-sm font-bold text-foreground-700">5,0</span>
              <span className="ml-auto inline-flex items-center gap-1.5">
                <GoogleWordmark className="text-sm" />
              </span>
            </div>

            <p className="mt-6 font-body text-base leading-relaxed text-foreground-800 whitespace-pre-line">
              {openReview.text}
            </p>

            <div className="mt-7 flex items-start gap-2.5 rounded-md border border-primary-200 bg-primary-50 px-4 py-3">
              <i className="ri-shield-check-line text-primary-600 text-lg mt-0.5" />
              <p className="font-body text-xs leading-relaxed text-foreground-600">
                Originalbewertung von Google, unverändert übernommen.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Big 5.0 also shown for screen readers consistency (site-wide truth) */}
      <span className="sr-only">{site.rating} von 5 Sternen bei Google</span>
    </section>
  );
}